import React, { useEffect, useState } from "react";
import { Info, Send, X } from "lucide-react";
import Toast from "../common/Toast";
import { useCreateNameChangeRequestMutation } from "../../services/namechange";

const getNameChangeErrorMessage = (error) => {
  const data = error?.data;

  if (typeof data === "string") return data;
  if (data && typeof data.message === "string") return data.message;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];
    if (typeof firstError === "string") return firstError;
    if (firstError && typeof firstError.msg === "string") return firstError.msg;
    if (firstError && typeof firstError.message === "string") {
      return firstError.message;
    }
  }
  if (typeof error?.error === "string") return error.error;

  return "Could not submit your name change request. Please try again.";
};

const emptyFieldErrors = {
  firstName: "",
  lastName: "",
  reason: "",
};

const CertificateNameCorrectionModal = ({ open, onClose, currentName }) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [reason, setReason] = useState("");
  const [fieldErrors, setFieldErrors] = useState(emptyFieldErrors);
  const [submitError, setSubmitError] = useState("");
  const [toast, setToast] = useState({
    show: false,
    title: "",
    message: "",
    variant: "default",
  });
  const [createNameChangeRequest, { isLoading }] =
    useCreateNameChangeRequestMutation();

  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setReason("");
      setFieldErrors(emptyFieldErrors);
      setSubmitError("");
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleClose = () => {
    setFieldErrors(emptyFieldErrors);
    setSubmitError("");
    onClose();
  };

  const validateForm = () => {
    const errors = { ...emptyFieldErrors };
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedReason = reason.trim();

    if (!trimmedFirstName) {
      errors.firstName = "Please enter your corrected first name.";
    } else if (trimmedFirstName.length < 3 || trimmedFirstName.length > 15) {
      errors.firstName = "First name must be 3–15 characters.";
    }

    if (!trimmedLastName) {
      errors.lastName = "Please enter your corrected last name.";
    } else if (trimmedLastName.length < 3 || trimmedLastName.length > 15) {
      errors.lastName = "Last name must be 3–15 characters.";
    }

    if (!trimmedReason) {
      errors.reason = "Please provide a reason for correction.";
    } else if (trimmedReason.length < 10 || trimmedReason.length > 40) {
      errors.reason = "Reason must be 10–40 characters.";
    }

    return errors;
  };

  const handleSubmit = async () => {
    setSubmitError("");

    const errors = validateForm();
    if (errors.firstName || errors.lastName || errors.reason) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors(emptyFieldErrors);

    try {
      const result = await createNameChangeRequest({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        reasonForCorrection: reason.trim(),
      }).unwrap();

      if (result && typeof result === "object" && result.success === false) {
        setSubmitError(
          typeof result.message === "string"
            ? result.message
            : "Could not submit your name change request.",
        );
        return;
      }

      setToast({
        show: true,
        title: "Request submitted",
        message:
          typeof result?.message === "string"
            ? result.message
            : "Your name correction request has been sent to the admin team for review.",
        variant: "success",
      });
      handleClose();
    } catch (error) {
      setSubmitError(getNameChangeErrorMessage(error));
    }
  };

  const inputErrorClass = (hasError) =>
    hasError
      ? "border-[#EF4444] focus:border-[#EF4444]"
      : "border-[#D1D5DB] focus:border-[#923D8C]";

  return (
    <>
      <div
        className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/40 p-4 sm:items-center"
        onClick={handleClose}
        role="presentation"
      >
        <div
          className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-[448px] flex-col overflow-hidden rounded-xl bg-white font-['Roboto']"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="name-correction-title"
        >
          <div className="flex shrink-0 items-center justify-between border-b border-[#E5E7EB] p-4 sm:p-6">
            <h2
              id="name-correction-title"
              className="pr-3 text-lg font-bold text-[#111827] sm:text-xl"
            >
              Request Certificate Name Correction
            </h2>

            <div className="shrink-0 p-1">
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close name correction request"
                className="text-[#6B7280] transition-opacity hover:opacity-70"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto">
            <div className="space-y-3 p-4 sm:p-6">
              <div>
                <label
                  htmlFor="current-certificate-name"
                  className="text-sm font-medium text-[#374151]"
                >
                  Current Name (from registration)
                </label>
                <input
                  id="current-certificate-name"
                  type="text"
                  value={currentName}
                  readOnly
                  className="mt-2 w-full rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-base text-[#111827] outline-none"
                />
              </div>

              <div>
                <label
                  htmlFor="corrected-first-name"
                  className="text-sm font-medium text-[#374151]"
                >
                  Corrected First Name <span className="text-[#EF4444]">*</span>
                </label>
                <input
                  id="corrected-first-name"
                  type="text"
                  value={firstName}
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    if (fieldErrors.firstName) {
                      setFieldErrors((current) => ({
                        ...current,
                        firstName: "",
                      }));
                    }
                    if (submitError) setSubmitError("");
                  }}
                  placeholder="Enter your corrected first name"
                  maxLength={15}
                  className={`mt-2 w-full rounded-lg border p-3 text-base text-[#111827] placeholder:text-[#9CA3AF] outline-none ${inputErrorClass(fieldErrors.firstName)}`}
                />
                {fieldErrors.firstName ? (
                  <p className="mt-1.5 text-sm text-[#EF4444]">
                    {fieldErrors.firstName}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="corrected-last-name"
                  className="text-sm font-medium text-[#374151]"
                >
                  Corrected Last Name <span className="text-[#EF4444]">*</span>
                </label>
                <input
                  id="corrected-last-name"
                  type="text"
                  value={lastName}
                  onChange={(event) => {
                    setLastName(event.target.value);
                    if (fieldErrors.lastName) {
                      setFieldErrors((current) => ({
                        ...current,
                        lastName: "",
                      }));
                    }
                    if (submitError) setSubmitError("");
                  }}
                  placeholder="Enter your corrected last name"
                  maxLength={15}
                  className={`mt-2 w-full rounded-lg border p-3 text-base text-[#111827] placeholder:text-[#9CA3AF] outline-none ${inputErrorClass(fieldErrors.lastName)}`}
                />
                {fieldErrors.lastName ? (
                  <p className="mt-1.5 text-sm text-[#EF4444]">
                    {fieldErrors.lastName}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="correction-reason"
                  className="text-sm font-medium text-[#374151]"
                >
                  Reason for Correction <span className="text-[#EF4444]">*</span>
                </label>
                <textarea
                  id="correction-reason"
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value);
                    if (fieldErrors.reason) {
                      setFieldErrors((current) => ({
                        ...current,
                        reason: "",
                      }));
                    }
                    if (submitError) setSubmitError("");
                  }}
                  placeholder="Explain why you need this name correction..."
                  rows={2}
                  maxLength={40}
                  className={`mt-2 w-full resize-none rounded-lg border px-3 py-2 text-xs text-[#1E3A8A] placeholder:text-[#9CA3AF] outline-none ${inputErrorClass(fieldErrors.reason)}`}
                />
                {fieldErrors.reason ? (
                  <p className="mt-1.5 text-sm text-[#EF4444]">
                    {fieldErrors.reason}
                  </p>
                ) : null}
              </div>

              <div className="flex gap-3 rounded-lg border border-[#FEF08A] bg-[#FEFCE8] p-[17px]">
                <Info size={18} className="mt-1 shrink-0 text-[#CA8A04]" />
                <div>
                  <p className="text-base font-medium text-[#854D0E]">
                    Review Process
                  </p>
                  <p className="mt-1 text-sm text-[#A16207]">
                    Correction requests will be reviewed by the admin team.
                    You&apos;ll receive an email notification once your request
                    is processed.
                  </p>
                </div>
              </div>

              {submitError ? (
                <p className="rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#DC2626]">
                  {submitError}
                </p>
              ) : null}

              <div className="flex flex-col gap-3 pt-1 sm:flex-row">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1 rounded-lg border border-[#D1D5DB] px-4 py-2 text-sm font-medium text-[#374151] transition-colors hover:bg-[#F9FAFB] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#F97316] to-[#9333EA] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send size={14} />
                  {isLoading ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast.show ? (
        <Toast
          title={toast.title}
          message={toast.message}
          variant={toast.variant}
          onClose={() => setToast((current) => ({ ...current, show: false }))}
        />
      ) : null}
    </>
  );
};

export default CertificateNameCorrectionModal;
